import React from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../utils/seo';

const PrivacyPolicy: React.FC = () => {
  useSEO({
    title: "Privacy Policy - Alertaio",
    description: "Read Alertaio's privacy policy to understand how we collect, use, and protect your data when using our AI API cost monitoring service.",
    keywords: "privacy policy, data protection, GDPR, SOC 2 compliance, data security",
    canonicalUrl: "https://alertaio.com/privacy",
    type: "website"
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <Link to="/" className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-semibold transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-xl p-8 md:p-12">
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4">Privacy Policy</h1>
          <p className="text-slate-600 mb-8">Effective Date: December 22, 2025</p>

          <div className="prose prose-slate max-w-none space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Introduction</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                Welcome to Alertaio ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service to monitor and receive alerts about your AI API costs.
              </p>
              <p className="text-slate-700 leading-relaxed">
                By using Alertaio, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our service.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-3">2.1 Information You Provide</h3>
              <ul className="list-disc list-inside space-y-2 text-slate-700 mb-4">
                <li><strong>Account Information:</strong> Name, email address, and password when you create an account</li>
                <li><strong>Payment Information:</strong> Billing details processed securely through our payment processor (we do not store full credit card numbers)</li>
                <li><strong>API Keys:</strong> Read-only API keys from OpenAI to monitor usage (expanding to other providers soon)</li>
                <li><strong>Budget Preferences:</strong> Your configured spending limits and alert thresholds</li>
                <li><strong>Communication Preferences:</strong> Email address, Slack webhook URLs, or phone numbers for receiving alerts</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mb-3">2.2 Automatically Collected Information</h3>
              <ul className="list-disc list-inside space-y-2 text-slate-700 mb-4">
                <li><strong>Usage Data:</strong> API cost data, usage patterns, and spending trends from your connected AI service providers</li>
                <li><strong>Log Data:</strong> IP address, browser type, operating system, access times, and pages viewed</li>
                <li><strong>Device Information:</strong> Device identifiers, mobile network information, and time zone settings</li>
                <li><strong>Cookies and Tracking:</strong> We use cookies and similar technologies to maintain session information and improve user experience</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mb-3">2.3 Information We DO NOT Collect</h3>
              <ul className="list-disc list-inside space-y-2 text-slate-700">
                <li><strong>AI Prompts or Responses:</strong> We never access, store, or view your AI prompts, completions, or any content processed through AI APIs</li>
                <li><strong>Full API Keys:</strong> We only use read-only keys that cannot modify or access your AI model interactions</li>
                <li><strong>Personal AI Data:</strong> Any personal or sensitive data processed through your AI applications</li>
              </ul>
            </section>

            {/* How We Use Your Information */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-slate-700 leading-relaxed mb-3">We use the collected information for the following purposes:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700">
                <li><strong>Service Delivery:</strong> Monitor your AI API usage and costs in real-time</li>
                <li><strong>Alerts and Notifications:</strong> Send you alerts when spending thresholds are reached</li>
                <li><strong>Account Management:</strong> Create and manage your account, process payments, and provide customer support</li>
                <li><strong>Service Improvement:</strong> Analyze usage patterns to improve our service and develop new features</li>
                <li><strong>Security:</strong> Detect, prevent, and address technical issues, fraud, and security threats</li>
                <li><strong>Communications:</strong> Send you service updates, security alerts, and marketing communications (you can opt-out)</li>
                <li><strong>Legal Compliance:</strong> Comply with applicable laws, regulations, and legal processes</li>
              </ul>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Data Security</h2>
              <p className="text-slate-700 leading-relaxed mb-3">
                We implement industry-standard security measures to protect your information:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700">
                <li><strong>Encryption:</strong> All data transmission uses TLS/SSL encryption</li>
                <li><strong>Data Storage:</strong> API keys and sensitive data are encrypted at rest using AES-256 encryption</li>
                <li><strong>Access Controls:</strong> Strict access controls and authentication requirements for our systems</li>
                <li><strong>Regular Audits:</strong> Security audits and vulnerability assessments</li>
                <li><strong>Monitoring:</strong> 24/7 monitoring for suspicious activities and potential breaches</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-4">
                However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            {/* Data Sharing and Disclosure */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Data Sharing and Disclosure</h2>
              <p className="text-slate-700 leading-relaxed mb-3">We do not sell your personal information. We may share your information only in the following circumstances:</p>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-3">5.1 Service Providers</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                We may share information with third-party service providers who perform services on our behalf, such as payment processing, email delivery, and cloud hosting. These providers are contractually obligated to protect your information and use it only for the purposes we specify.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mb-3">5.2 Legal Requirements</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                We may disclose your information if required by law, court order, or governmental regulation, or if we believe disclosure is necessary to protect our rights, your safety, or the safety of others.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mb-3">5.3 Business Transfers</h3>
              <p className="text-slate-700 leading-relaxed">
                In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity. We will notify you of any such change in ownership or control.
              </p>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Data Retention</h2>
              <p className="text-slate-700 leading-relaxed mb-3">We retain your information for as long as necessary to:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700">
                <li>Provide our services and maintain your account</li>
                <li>Comply with legal obligations and resolve disputes</li>
                <li>Maintain business records for financial and tax purposes</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-4">
                When you close your account, we will delete or anonymize your personal information within 90 days, except where retention is required by law or for legitimate business purposes.
              </p>
            </section>

            {/* Your Privacy Rights */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Your Privacy Rights</h2>
              <p className="text-slate-700 leading-relaxed mb-3">Depending on your location, you may have the following rights:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal exceptions)</li>
                <li><strong>Portability:</strong> Request transfer of your data to another service provider</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
                <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
                <li><strong>Object:</strong> Object to processing based on legitimate interests</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-4">
                To exercise these rights, please contact us at <a href="mailto:privacy@alertaio.com" className="text-indigo-600 hover:text-indigo-700 font-semibold">privacy@alertaio.com</a>. We will respond to your request within 30 days.
              </p>
            </section>

            {/* Cookies and Tracking */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Cookies and Tracking Technologies</h2>
              <p className="text-slate-700 leading-relaxed mb-3">We use cookies and similar tracking technologies to:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700">
                <li>Maintain your session and remember your preferences</li>
                <li>Analyze usage patterns and improve our service</li>
                <li>Provide personalized content and recommendations</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-4">
                You can control cookies through your browser settings. However, disabling cookies may limit your ability to use certain features of our service.
              </p>
            </section>

            {/* International Data Transfers */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">9. International Data Transfers</h2>
              <p className="text-slate-700 leading-relaxed">
                Your information may be transferred to and processed in countries other than your country of residence. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards, such as standard contractual clauses approved by regulatory authorities.
              </p>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Children's Privacy</h2>
              <p className="text-slate-700 leading-relaxed">
                Our service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately, and we will delete such information.
              </p>
            </section>

            {/* Changes to Privacy Policy */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Changes to This Privacy Policy</h2>
              <p className="text-slate-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of material changes by posting the new policy on our website and updating the "Effective Date." Your continued use of our service after such changes constitutes acceptance of the updated policy.
              </p>
            </section>

            {/* Contact Us */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Contact Us</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <p className="text-slate-700 mb-2"><strong>Email:</strong> <a href="mailto:privacy@alertaio.com" className="text-indigo-600 hover:text-indigo-700">privacy@alertaio.com</a></p>
                <p className="text-slate-700 mb-2"><strong>Support:</strong> <a href="mailto:support@alertaio.com" className="text-indigo-600 hover:text-indigo-700">support@alertaio.com</a></p>
                <p className="text-slate-700"><strong>Website:</strong> <a href="https://alertaio.com" className="text-indigo-600 hover:text-indigo-700">alertaio.com</a></p>
              </div>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-slate-200">
            <Link to="/" className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-semibold transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
